import React, { useEffect, useRef, useState } from 'react';

/**
 * LazyBlobImage — fetches a blob URL only when near the viewport and revokes
 * it when the element scrolls well out of view. Used to keep memory low when
 * a page holds many captures / appendix images.
 *
 * Props:
 *   fetchBlob  – async () => string (blob URL) — typically wraps a fetchXxxBlob helper
 *   alt        – string
 *   className  – string
 *   placeholder – optional React node rendered while idle / loading / unloaded
 *
 * Tuning: near-margin 300px (prefetch), far-margin –300px (unload once ~one
 * viewport past). Tweak UNLOAD_DELAY_MS to bias toward keeping vs dropping.
 */
const PREFETCH_MARGIN = '300px';
const UNLOAD_DELAY_MS = 1500;

export default function LazyBlobImage({ fetchBlob, alt, className, placeholder = null }) {
  const wrapperRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const unloadTimer = useRef(null);
  const activeFetch = useRef(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback: fetch immediately
      let live = true;
      (async () => {
        const url = await fetchBlob();
        if (live) setBlobUrl(url);
      })();
      return () => { live = false; };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          if (unloadTimer.current) {
            clearTimeout(unloadTimer.current);
            unloadTimer.current = null;
          }
          setBlobUrl((current) => {
            if (current) return current;
            const fetchId = ++activeFetch.current;
            fetchBlob().then((url) => {
              if (fetchId !== activeFetch.current) {
                // Stale fetch — a newer load took over; revoke this one
                if (url) URL.revokeObjectURL(url);
                return;
              }
              setBlobUrl(url);
            }).catch(() => {});
            return current;
          });
        } else {
          // Schedule unload after delay (avoids flicker on fast scroll back)
          if (unloadTimer.current) clearTimeout(unloadTimer.current);
          unloadTimer.current = setTimeout(() => {
            setBlobUrl((current) => {
              if (current) URL.revokeObjectURL(current);
              return null;
            });
            activeFetch.current++; // invalidate any in-flight fetch
          }, UNLOAD_DELAY_MS);
        }
      },
      { rootMargin: PREFETCH_MARGIN }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (unloadTimer.current) clearTimeout(unloadTimer.current);
    };
    // fetchBlob is a new function each render in some cases — intentionally exclude.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  return (
    <div ref={wrapperRef} className="lazy-blob-wrapper">
      {blobUrl ? (
        <img src={blobUrl} alt={alt} className={className} />
      ) : (
        placeholder ?? <div className="cg-image-placeholder" />
      )}
    </div>
  );
}
